import { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { AdminPageShell, SortIcon, createSortHelpers } from '../components/AdminShared';

const LN = { en:'English',es:'Spanish',zh:'Mandarin',ar:'Arabic',vi:'Vietnamese',fr:'French',tl:'Tagalog',ko:'Korean',ru:'Russian',pt:'Portuguese',hi:'Hindi' };

export default function AdminSessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgFilter, setOrgFilter] = useState('');
  const [langFilter, setLangFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    Promise.all([adminAPI.getSessions(), adminAPI.listOrgs()])
      .then(([sRes, oRes]) => { setSessions(sRes.data); setOrgs(oRes.data.sort((a, b) => a.name.localeCompare(b.name))); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const { toggleSort } = createSortHelpers(sortCol, sortDir, setSortCol, setSortDir);

  const filtered = useMemo(() => {
    let list = sessions.filter(s => {
      if (orgFilter && s.org_id !== orgFilter) return false;
      if (langFilter && s.target_language !== langFilter && s.source_language !== langFilter) return false;
      if (statusFilter && s.status !== statusFilter) return false;
      if (dateFrom && s.created_at < dateFrom) return false;
      if (dateTo && s.created_at > dateTo + 'T23:59:59') return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      let va = '', vb = '';
      if (sortCol === 'org') { va = a.org_name || ''; vb = b.org_name || ''; }
      else if (sortCol === 'lang') { va = a.target_language || ''; vb = b.target_language || ''; }
      else if (sortCol === 'status') { va = a.status || ''; vb = b.status || ''; }
      else if (sortCol === 'type') { va = (a.escalated || a.human_interpreter_id || a.session_type === 'human') ? 'b' : 'a'; vb = (b.escalated || b.human_interpreter_id || b.session_type === 'human') ? 'b' : 'a'; }
      else if (sortCol === 'domain') { va = a.domain_level || ''; vb = b.domain_level || ''; }
      else if (sortCol === 'advisory') { va = a.advisory_state || ''; vb = b.advisory_state || ''; }
      else if (sortCol === 'credits') { va = String(a.credits_used || 0); vb = String(b.credits_used || 0); }
      else if (sortCol === 'date') { va = a.created_at || ''; vb = b.created_at || ''; }
      else if (sortCol === 'ai') { va = String(a.ai_seconds || 0).padStart(8, '0'); vb = String(b.ai_seconds || 0).padStart(8, '0'); }
      const cmp = va.localeCompare(vb);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [sessions, orgFilter, langFilter, statusFilter, dateFrom, dateTo, sortCol, sortDir]);

  const uniqueLangs = [...new Set(sessions.flatMap(s => [s.source_language, s.target_language]))].filter(Boolean).sort();

  if (loading) return <AdminPageShell loading={true} />;

  return (
    <AdminPageShell title={`Sessions (${filtered.length})`} testId="admin-sessions-page">

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <Select value={orgFilter} onValueChange={v => setOrgFilter(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-48" data-testid="session-org-filter"><SelectValue placeholder="All Organizations" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Organizations</SelectItem>
            {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={langFilter} onValueChange={v => setLangFilter(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-40" data-testid="session-lang-filter"><SelectValue placeholder="All Languages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Languages</SelectItem>
            {uniqueLangs.map(l => <SelectItem key={l} value={l}>{LN[l] || l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-36" data-testid="session-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="ended">Completed</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36 text-xs" placeholder="From" data-testid="session-date-from" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36 text-xs" placeholder="To" data-testid="session-date-to" />
        </div>
        {(orgFilter || langFilter || statusFilter || dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setOrgFilter(''); setLangFilter(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); }} data-testid="clear-session-filters">Clear</Button>
        )}
      </div>

      <div className="rounded-lg border bg-card"><ScrollArea className="max-h-[600px]"><Table><TableHeader><TableRow>
        <TableHead className="font-mono text-xs">ID</TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('org')} data-testid="sort-session-org">Organization<SortIcon col="org" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('lang')} data-testid="sort-session-lang">Languages<SortIcon col="lang" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('status')}>Status<SortIcon col="status" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('type')}>Call Type<SortIcon col="type" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('domain')}>Domain<SortIcon col="domain" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('advisory')}>Advisory<SortIcon col="advisory" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none font-mono text-xs" onClick={() => toggleSort('credits')}>Credits<SortIcon col="credits" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none font-mono text-xs" onClick={() => toggleSort('ai')}>AI Sec<SortIcon col="ai" sortCol={sortCol} sortDir={sortDir} /></TableHead>
        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('date')}>Date<SortIcon col="date" sortCol={sortCol} sortDir={sortDir} /></TableHead>
      </TableRow></TableHeader><TableBody>
        {filtered.map(s => (
          <TableRow key={s.id}>
            <TableCell className="font-mono text-xs">{s.id.slice(0,8)}</TableCell>
            <TableCell className="text-sm"><Badge variant="outline" className="text-xs">{s.org_name || '-'}</Badge></TableCell>
            <TableCell className="text-sm">{LN[s.source_language] || s.source_language} &rarr; {LN[s.target_language] || s.target_language}</TableCell>
            <TableCell><Badge variant={s.status === 'active' ? 'default' : s.status === 'human_connected' ? 'outline' : 'secondary'} className={`text-xs capitalize ${s.status === 'ended' ? 'bg-green-500/10 text-green-600 border-green-500/20' : ''}`}>{s.status === 'ended' ? 'Completed' : s.status === 'human_connected' ? 'Human' : s.status}</Badge></TableCell>
            <TableCell><Badge variant="outline" className="text-xs">{s.escalated || s.human_interpreter_id || s.session_type === 'human' ? 'AI+Human' : 'AI Only'}</Badge></TableCell>
            <TableCell className="text-xs font-mono">{s.domain_level}</TableCell>
            <TableCell><Badge variant={s.advisory_state === 'NONE' ? 'secondary' : s.advisory_state === 'ADVISORY' ? 'outline' : 'destructive'} className="text-xs">{s.advisory_state}</Badge></TableCell>
            <TableCell className="font-mono text-xs">{s.credits_used || '-'}</TableCell>
            <TableCell className="font-mono text-xs">{s.ai_seconds}s</TableCell>
            <TableCell className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</TableCell>
          </TableRow>
        ))}
        {filtered.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">No sessions found</TableCell></TableRow>}
      </TableBody></Table></ScrollArea></div>
    </AdminPageShell>
  );
}
